import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ResetModal, hasAttempt } from '../../AdminDashboardV3/DiagnosticMonitor';
import { DIAGNOSTIC_IDS, diagnosticLabel } from '../../Diagnostic/diagnosticAvailability';
import { Modal, ModalHeader, Button } from './ui';
import { DLG } from './dialogStyles';
import { fetchClasses, fetchDiagnosticLive, resetStudentDiagnostic } from '../data/api';

// One student's row on each diagnostic, read the way the Diagnostic page reads
// it (get_monitor_detail, school-scoped). A failed read leaves that test null.
async function studentDiagnosticRows(auth, classId, userId) {
  const classes = await fetchClasses(auth);
  const cls = (classes || []).find((c) => c.id === classId || c.classID === classId);
  const schoolName = cls?.school || undefined;
  return Promise.all(DIAGNOSTIC_IDS.map((assignmentId) => fetchDiagnosticLive(auth, { assignmentId, schoolName, studentIds: [userId] })
    .then((live) => (live?.students || []).find((s) => s.studentId === userId) || null)
    .catch(() => null)));
}

const statusOf = (row) => (row?.status === 'Done' ? 'Finished' : row?.timerStatus || row?.started ? 'In progress' : 'Has saved scores');

// "Which diagnostic?" step, shown only when more than one test has an attempt
// (or the attempts could not be read: then all three, so the teacher can act).
export function DiagnosticChooser({ name, options, onPick, onClose }) {
  return (
    <Modal open onClose={onClose} size="sm">
      <ModalHeader title="Which diagnostic?" sub={name} pad={DLG.head} onClose={onClose} />
      <div className={`${DLG.body} flex flex-col gap-[8px]`}>
        {options.map((o) => (
          <button key={o.id} type="button" onClick={() => onPick(o.id)} className={DLG.row}>
            <span className="block text-[14px] font-semibold text-ink">{diagnosticLabel(o.id)}</span>
            {o.status && <span className="block text-[12px] text-neutral-500">{o.status}</span>}
          </button>
        ))}
      </div>
    </Modal>
  );
}

/* Reset one student's diagnostic from Buffy's chat (2026-10-06). Same dialog
   and same call as Diagnostic → student → Reset test (ResetModal +
   resetStudentDiagnostic, which pauses a student who is mid-test first).
   `test` is 1, 2 or 3 as teachers see it. Without one, the dialog looks at
   which tests the student attempted (the page's hasAttempt rule): one → that
   test's reset; several → the teacher picks among them; none → nothing to reset. */
export default function ResetDiagnosticDialog({ student, test, auth, classId, onClose, onDone }) {
  const preset = DIAGNOSTIC_IDS[Number(test) - 1] || null;
  const [assignmentId, setAssignmentId] = useState(preset);
  const [rows, setRows] = useState(null); // null = loading (only when no preset)
  const [stage, setStage] = useState(null); // null | 'working' | 'pausing'
  const [error, setError] = useState(null);
  useEffect(() => {
    if (preset) return undefined;
    let alive = true;
    studentDiagnosticRows(auth, classId, student.userId)
      .then((r) => {
        if (!alive) return;
        setRows(r);
        const tried = DIAGNOSTIC_IDS.filter((id, i) => r[i] && hasAttempt(r[i]));
        if (tried.length === 1) setAssignmentId(tried[0]);
      })
      .catch(() => { if (alive) setRows([null, null, null]); });
    return () => { alive = false; };
  }, [preset, auth, classId, student.userId]);

  if (!assignmentId) {
    if (!rows) {
      return (
        <Modal open onClose={onClose} size="sm">
          <ModalHeader title="Reset a diagnostic" sub={student.name} pad={DLG.head} onClose={onClose} />
          <div className={DLG.body}>
            <p role="status" className="m-0 text-[13px] text-neutral-700 leading-relaxed flex items-center gap-2">
              <Loader2 size={14} className="animate-spin flex-none" aria-hidden="true" />
              Checking {student.name}&apos;s diagnostics…
            </p>
          </div>
        </Modal>
      );
    }
    const readable = rows.some(Boolean);
    const tried = DIAGNOSTIC_IDS.map((id, i) => ({ id, row: rows[i] })).filter((o) => o.row && hasAttempt(o.row));
    if (readable && !tried.length) {
      return (
        <Modal open onClose={onClose} size="sm">
          <ModalHeader title="Reset a diagnostic" sub={student.name} pad={DLG.head} onClose={onClose} />
          <div className={DLG.body}>
            <p role="status" className="m-0 text-[13px] text-neutral-700 leading-relaxed">{student.name} hasn&apos;t started a diagnostic, so there&apos;s nothing to reset.</p>
          </div>
          <div className={DLG.foot}>
            <Button variant="secondary" size="dlg" onClick={onClose}>Close</Button>
          </div>
        </Modal>
      );
    }
    const options = readable
      ? tried.map((o) => ({ id: o.id, status: statusOf(o.row) }))
      : DIAGNOSTIC_IDS.map((id) => ({ id, status: null }));
    return <DiagnosticChooser name={student.name} options={options} onPick={setAssignmentId} onClose={onClose} />;
  }

  const testLabel = diagnosticLabel(assignmentId);
  const row = rows ? rows[DIAGNOSTIC_IDS.indexOf(assignmentId)] : null;
  const confirm = async (reason) => {
    setStage('working');
    setError(null);
    try {
      await resetStudentDiagnostic(auth, classId, student.userId, assignmentId, reason, { onPausing: () => setStage('pausing') });
      onDone(`Reset ${student.name}'s ${testLabel}. They can retake it.`, `${testLabel} reset for ${student.name}`);
      onClose();
    } catch (e) {
      setError(e?.message || 'Reset failed. Try again.');
    } finally {
      setStage(null);
    }
  };
  return (
    <ResetModal
      s={{ ...(row || {}), name: student.name, studentId: student.userId }}
      testLabel={testLabel}
      stage={stage}
      error={error}
      onClose={onClose}
      onConfirm={confirm}
    />
  );
}
